import { useEffect, useState, useCallback } from 'react';
import { ExternalRefsCard } from '@/components/inventory/ExternalRefsCard';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Package,
  MapPin,
  AlertTriangle,
  Edit,
  RefreshCw,
  History,
  ArrowUpRight,
  ArrowDownLeft,
  ShieldCheck,
  Truck,
  ClipboardList,
  Layers,
  Wrench,
  BarChart3,
  Clock,
  ShieldAlert,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Gauge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  KPIGrid,
  KPIStrip,
  LoadingState,
} from '@/components/common';
import {
  inventoryApi,
  InventoryItem,
  InventoryLevel,
  InventoryTransaction,
  TransactionSummary,
  ConsumptionPattern,
  JobTypeRate,
  SafetyStockCalculation,
  StockReservation,
  SupplyPipeline,
  type InboundShipment,
  type BinLocation,
  ItemBatchData,
  ItemBatch,
  ItemEquipmentLinks,
  CriticalSpareRanking,
  EquipmentLink,
} from '@/api/services';
import { TabCount } from '@/components/common/TabCount';
import { getErrorMessage } from '@/api/client';
import { cn, formatCurrency, formatNumber, formatDate, pluralize, toTitleCase } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { chartColors } from '@/lib/design-tokens';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { DetailLayout, DetailList, MetricBars, RailCard } from '@/components/common/DetailPanels';

const statusMap: Record<string, 'optimal' | 'low' | 'stockout' | 'overstock'> = {
  Optimal: 'optimal',
  Low: 'low',
  Stockout: 'stockout',
  Overstock: 'overstock',
};

const locationTypeColors: Record<string, string> = {
  HUB: 'bg-status-info-subtle text-status-info-foreground',
  SITE: 'bg-status-success-subtle text-status-success-foreground',
  DEPOT: 'bg-status-warning-subtle text-status-warning-foreground',
  // No purple in the status palette; a supplier is an external party, so it
  // reads as neutral rather than borrowing another tone's meaning.
  SUPPLIER: 'bg-status-neutral-subtle text-status-neutral-foreground',
};

/** A = highest value/criticality, so it carries the strongest tone. */
const abcColors: Record<string, string> = {
  A: 'bg-status-destructive-subtle text-status-destructive-foreground border-status-destructive-border',
  B: 'bg-status-warning-subtle text-status-warning-foreground border-status-warning-border',
  C: 'bg-status-success-subtle text-status-success-foreground border-status-success-border',
};

export default function InventoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [levels, setLevels] = useState<InventoryLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stock-levels');

  // Lazy-loaded tab data
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [txnSummary, setTxnSummary] = useState<TransactionSummary | null>(null);
  const [consumptionPattern, setConsumptionPattern] = useState<ConsumptionPattern | null>(null);
  const [jobTypeRates, setJobTypeRates] = useState<JobTypeRate[]>([]);
  const [safetyStock, setSafetyStock] = useState<SafetyStockCalculation | null>(null);
  const [supplyPipeline, setSupplyPipeline] = useState<SupplyPipeline | null>(null);
  const [reservations, setReservations] = useState<StockReservation[]>([]);
  const [batchData, setBatchData] = useState<ItemBatchData | null>(null);
  const [equipmentLinks, setEquipmentLinks] = useState<ItemEquipmentLinks | null>(null);
  const [spareRanking, setSpareRanking] = useState<CriticalSpareRanking | null>(null);

  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());

  /** Per-tab record counts. null means not known yet, so nothing is shown. */
  const [tabCounts, setTabCounts] = useState<Record<string, number | null>>({});

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [itemData, levelsData] = await Promise.all([
        inventoryApi.getById(id),
        inventoryApi.getLevels(id),
      ]);
      setItem(itemData);
      setLevels(levelsData);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  // Tab counts, fetched once the item is known.
  //
  // The tab bodies stay lazy — transactions pull 50 rows and usage pulls
  // consumption analytics, which is why that was built. But a row of seven tabs
  // with no counts on any of them means the only way to find out that
  // Reservations is empty is to open it, and the same for the other six. These
  // five calls return summaries rather than content, so the counts are cheap
  // even though the tabs they describe are not.
  useEffect(() => {
    if (!id || !item) return;
    let cancelled = false;

    (async () => {
      const [pipeline, res, batches, links, txns, pattern, safety, recentTxns, ranking] =
        await Promise.all([
          inventoryApi.getSupplyPipeline(id).catch(() => null),
          inventoryApi.getReservations({ itemId: id }).catch(() => ({ data: [] })),
          inventoryApi.getItemBatches(id).catch(() => null),
          inventoryApi.getItemEquipmentLinks(id).catch(() => null),
          inventoryApi.getTransactionSummary(id).catch(() => null),
          // The four below feed the rail rather than a tab. The rail is on
          // screen from the first paint, so its data cannot wait for a tab to
          // be opened — a card that appears several clicks later reads as the
          // page being broken, and a rail that silently omits a card reads as
          // the item having no lead time or no movement.
          inventoryApi.getItemConsumptionPattern(id).catch(() => null),
          inventoryApi.getItemSafetyStock(id).catch(() => null),
          inventoryApi.getTransactions(id, { limit: 30 }).catch(() => ({ data: [] })),
          // Why this item matters when it fails. Critical Spares ranks items on
          // exactly this and then linked every row here, where none of it was
          // shown — so opening a row from that page threw away the analysis it
          // came from. Returns a ranking for any item, not only listed spares.
          inventoryApi.getSparePartRanking(id).catch(() => null),
        ]);
      if (cancelled) return;

      // The pipeline was already being fetched here for the tab count and then
      // thrown away, so the supply-pipeline tab fetched it a second time.
      setSupplyPipeline(pipeline);
      setConsumptionPattern(pattern);
      setSafetyStock(safety);
      setTransactions(recentTxns.data ?? []);
      setSpareRanking(ranking);

      setTabCounts({
        // `|| null` would collapse a real zero into "unknown", so the tab would
        // show nothing where Reservations correctly shows 0. Only a failed
        // request counts as unknown.
        'supply-pipeline': pipeline
          ? (pipeline.summary?.onOrder ?? 0) + (pipeline.summary?.inTransit ?? 0)
          : null,
        transactions: txns?.totalTransactions ?? null,
        reservations: res?.data?.length ?? null,
        batches: batches?.summary?.total ?? null,
        equipment: links?.summary?.totalEquipment ?? null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [id, item]);

  // Load tab data when activeTab changes or on mount
  const loadTabData = useCallback(
    async (tab: string) => {
      if (!id || loadedTabs.has(tab)) return;
      setLoadedTabs((prev) => new Set(prev).add(tab));

      try {
        switch (tab) {
          case 'usage-trends': {
            const [pattern, rates, safety] = await Promise.all([
              inventoryApi.getItemConsumptionPattern(id).catch(() => null),
              inventoryApi.getJobTypeRates(id).catch(() => []),
              inventoryApi.getItemSafetyStock(id).catch(() => null),
            ]);
            setConsumptionPattern(pattern);
            setJobTypeRates(rates);
            setSafetyStock(safety);
            break;
          }
          case 'supply-pipeline': {
            const pipeline = await inventoryApi.getSupplyPipeline(id).catch(() => null);
            setSupplyPipeline(pipeline);
            break;
          }
          case 'transactions': {
            setTransactionsLoading(true);
            const [txnResult, summary] = await Promise.all([
              inventoryApi.getTransactions(id, { limit: 50 }),
              inventoryApi.getTransactionSummary(id).catch(() => null),
            ]);
            setTransactions(txnResult.data);
            setTxnSummary(summary);
            setTransactionsLoading(false);
            break;
          }
          case 'reservations': {
            const res = await inventoryApi
              .getReservations({ itemId: id })
              .catch(() => ({ data: [] }));
            setReservations(res.data || []);
            break;
          }
          case 'batches': {
            const batches = await inventoryApi.getItemBatches(id).catch(() => null);
            setBatchData(batches);
            break;
          }
          case 'equipment': {
            const links = await inventoryApi.getItemEquipmentLinks(id).catch(() => null);
            setEquipmentLinks(links);
            break;
          }
        }
      } catch (error) {
        toast.error('Failed to load tab data');
      }
    },
    [id, loadedTabs]
  );

  useEffect(() => {
    if (item) {
      loadTabData(activeTab);
    }
  }, [activeTab, item, loadTabData]);

  // Lazy-load tab data

  if (loading) return <LoadingState />;
  if (!item) return null;

  const totalQuantity =
    item.aggregated?.totalQuantity || levels.reduce((sum, l) => sum + l.quantity, 0);
  const totalReserved =
    item.aggregated?.totalReserved || levels.reduce((sum, l) => sum + l.reservedQty, 0);
  const totalAvailable = item.aggregated?.totalAvailable || totalQuantity - totalReserved;
  const totalValue = totalQuantity * (item.unitCost || 0);

  // Cover comes from the server, off the stock ledger, so this page and the
  // inventory list quote the same number. It used to be derived here from the
  // consumption-pattern service — a different ledger over a different window —
  // and the same item read 245 days in the list against 490 here.
  const daysOfStock = item.aggregated?.daysOfStock ?? null;
  // The card below already told the reader this was a 90-day average while the
  // figure behind it came from a 180-day window; now it is one.
  const avgDailyConsumption = item.aggregated?.avgDailyConsumption ?? 0;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Inventory', href: '/inventory' },
            { label: item.sku },
          ]}
          title={item.name}
          description={`SKU: ${item.sku}`}
          // Stock status, ABC class and the regulated flag read on the title
          // line. Status in particular was only visible in the Item Details
          // table, well below the fold of the KPI strip.
          titleAdornment={
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={statusMap[item.status || 'Optimal'] || 'optimal'} />
              {item.abcClassification && (
                <Badge
                  variant="outline"
                  className={cn('border', abcColors[item.abcClassification])}
                >
                  Class {item.abcClassification}
                </Badge>
              )}
              {item.isRegulated && (
                <Badge
                  variant="outline"
                  className="border-status-warning-border bg-status-warning-subtle text-status-warning-foreground"
                >
                  <ShieldCheck className="mr-1 h-3 w-3" />
                  Regulated
                </Badge>
              )}
              {item.isActive === false && <Badge variant="secondary">Inactive</Badge>}
            </div>
          }
          actions={
            <div className="flex gap-2">
              <Button variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Restock
              </Button>
              <Button variant="outline">
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* KPI Row: 6 cards, single line */}
      <div className="mt-6">
        <KPIStrip columns={6}>
          <MetricCard
            label="Total Quantity"
            value={totalQuantity}
            suffix={` ${item.unit}`}
            sentiment="info"
            icon={<Package className="h-5 w-5" />}
            subtitle={`${levels.length} ${pluralize(levels.length, 'location')}`}
          />
          <MetricCard
            label="Available"
            value={totalAvailable}
            suffix={` ${item.unit}`}
            sentiment={
              item.reorderPoint && totalAvailable < item.reorderPoint ? 'danger' : 'success'
            }
            subtitle={item.reorderPoint ? `Reorder at ${item.reorderPoint}` : undefined}
          />
          <MetricCard
            label="Reserved"
            value={totalReserved}
            suffix={` ${item.unit}`}
            sentiment={totalReserved > 0 ? 'warning' : 'neutral'}
            subtitle={
              totalQuantity > 0
                ? `${((totalReserved / totalQuantity) * 100).toFixed(1)}% of total`
                : undefined
            }
          />
          <MetricCard
            label="Total Value"
            value={totalValue}
            prefix="$"
            sentiment="info"
            icon={<DollarSign className="h-5 w-5" />}
            subtitle={`Unit: ${formatCurrency(item.unitCost || 0)}`}
          />
          <MetricCard
            label="Days of Stock"
            value={daysOfStock !== null ? daysOfStock : '—'}
            suffix={daysOfStock !== null ? ' days' : ''}
            sentiment={
              daysOfStock === null
                ? 'neutral'
                : daysOfStock < 30
                  ? 'danger'
                  : daysOfStock < 60
                    ? 'warning'
                    : 'success'
            }
            icon={<Clock className="h-5 w-5" />}
            subtitle={
              avgDailyConsumption > 0
                ? `${avgDailyConsumption.toFixed(1)}/day avg`
                : 'No consumption data'
            }
          />
          <MetricCard
            label="Safety Stock"
            value={
              safetyStock
                ? totalQuantity >= safetyStock.calculatedSafetyStock
                  ? 'Adequate'
                  : 'Below'
                : '—'
            }
            sentiment={
              !safetyStock
                ? 'neutral'
                : totalQuantity >= safetyStock.calculatedSafetyStock
                  ? 'success'
                  : 'danger'
            }
            icon={<ShieldCheck className="h-5 w-5" />}
            subtitle={
              safetyStock
                ? `Rec: ${safetyStock.calculatedSafetyStock} ${item.unit}`
                : 'Not calculated'
            }
          />
        </KPIStrip>
      </div>

      {/* Main Tabs
          Two columns: the left carries the work and changes with the tab, the
          right carries what the item is and does not. */}
      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <ItemRail
              item={item}
              levels={levels}
              transactions={transactions}
              pipeline={supplyPipeline}
              pattern={consumptionPattern}
              safetyStock={safetyStock}
              spareRanking={spareRanking}
              totalQuantity={totalQuantity}
              daysOfStock={daysOfStock}
            />
          }
          main={
            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                setActiveTab(v);
                loadTabData(v);
              }}
            >
              <TabsList>
                <TabsTrigger value="stock-levels">
                  Stock Levels
                  <TabCount value={levels.length} />
                </TabsTrigger>
                <TabsTrigger value="usage-trends">Usage &amp; Trends</TabsTrigger>
                <TabsTrigger value="supply-pipeline">
                  Supply Pipeline
                  <TabCount value={tabCounts['supply-pipeline']} />
                </TabsTrigger>
                <TabsTrigger value="transactions">
                  Transactions
                  <TabCount value={tabCounts.transactions} />
                </TabsTrigger>
                <TabsTrigger value="reservations">
                  Reservations
                  <TabCount value={tabCounts.reservations} />
                </TabsTrigger>
                <TabsTrigger value="batches">
                  Batch &amp; Compliance
                  <TabCount value={tabCounts.batches} />
                </TabsTrigger>
                <TabsTrigger value="equipment">
                  Equipment &amp; Spares
                  <TabCount value={tabCounts.equipment} />
                </TabsTrigger>
              </TabsList>

              {/* ===== TAB 1: STOCK LEVELS (Enhanced) ===== */}
              <TabsContent value="stock-levels" className="space-y-4">
                <ExternalRefsCard itemId={item.id} />
                {/* Stock Level Gauge */}
                <StockGauge
                  current={totalQuantity}
                  min={item.minStockLevel}
                  reorderPoint={item.reorderPoint}
                  max={item.maxStockLevel}
                  unit={item.unit}
                  status={item.status}
                />

                <div className="mt-4 space-y-6">
                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold text-foreground">Stock by location</h3>
                      <CardDescription>Inventory levels across all locations</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {levels.length === 0 ? (
                        <EmptyState
                          icon={AlertTriangle}
                          title="No stock assigned"
                          description="Add this item to a location to track stock levels."
                        />
                      ) : (
                        <DataTable
                          data={levels}
                          columns={getLevelColumns(item)}
                          emptyTitle="No locations"
                          emptyDescription="No stock assigned."
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* What is already on its way, next to where it is going. Reading
                  a low location without knowing a replacement lands on Friday
                  is what produces a duplicate order. */}
                  {(supplyPipeline?.purchaseOrders?.length ?? 0) > 0 && (
                    <Card>
                      <CardHeader>
                        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                          <Truck className="h-5 w-5" />
                          Incoming shipments
                        </h3>
                        <CardDescription>
                          Open purchase orders expected against this item
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {supplyPipeline!.purchaseOrders.slice(0, 5).map((po) => (
                          <div
                            key={po.id}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium">
                                <Link
                                  to={`/purchase-orders/${po.poId}`}
                                  className="hover:underline"
                                >
                                  {po.poNumber}
                                </Link>
                                {po.supplier && (
                                  <span className="font-normal text-muted-foreground">
                                    {' '}
                                    · {po.supplier.name}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {po.expectedDate
                                  ? `Expected ${formatDate(po.expectedDate)}`
                                  : 'No expected date'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold tabular-nums">
                                {formatNumber(po.quantity - po.receivedQty)} {item.unit}
                              </p>
                              <Badge variant="outline" className="mt-0.5 text-[10px]">
                                {toTitleCase(po.status)}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* ===== TAB 2: USAGE & TRENDS ===== */}
              <TabsContent value="usage-trends">
                <div className="space-y-6 mt-4">
                  {/* Projected Stockout Alert */}
                  {daysOfStock !== null && (
                    <Card
                      className={`border-l-4 ${daysOfStock < 30 ? 'border-l-status-destructive bg-status-destructive-subtle' : daysOfStock < 60 ? 'border-l-status-warning bg-status-warning-subtle' : 'border-l-status-success bg-status-success-subtle'}`}
                    >
                      <CardContent className="py-4 flex items-center gap-3">
                        <Clock
                          className={`h-5 w-5 ${daysOfStock < 30 ? 'text-status-destructive' : daysOfStock < 60 ? 'text-status-warning' : 'text-status-success'}`}
                        />
                        <div>
                          <p className="font-medium">
                            Estimated <span className="text-lg font-bold">{daysOfStock} days</span>{' '}
                            of stock remaining at current usage
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Based on 90-day average consumption of {avgDailyConsumption.toFixed(2)}{' '}
                            {item.unit}/day
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Stock Balance Over Time (Area Chart) */}
                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Stock Balance Over Time</h3>
                      <CardDescription>
                        Balance after each transaction (last 50 transactions)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {transactions.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart
                            data={buildBalanceChartData(transactions)}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 12 }}
                              className="fill-muted-foreground"
                            />
                            <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                            <RechartsTooltip
                              contentStyle={{
                                borderRadius: 8,
                                border: '1px solid var(--border)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                              }}
                              labelStyle={{ fontWeight: 600 }}
                            />
                            {item.reorderPoint && (
                              <ReferenceLine
                                y={item.reorderPoint}
                                stroke={chartColors.danger}
                                strokeDasharray="5 5"
                                label={{ value: 'Reorder Point', position: 'right', fontSize: 12 }}
                              />
                            )}
                            <Area
                              type="monotone"
                              dataKey="balance"
                              stroke={chartColors.primary}
                              fill={chartColors.primary}
                              fillOpacity={0.12}
                              strokeWidth={2}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <EmptyState
                          icon={BarChart3}
                          title="No transaction data"
                          description="Switch to Transactions tab first to load data, then return here."
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* Consumption & Job Type Breakdown */}
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <h3 className="text-base font-semibold">Consumption Pattern</h3>
                        <CardDescription>
                          {consumptionPattern
                            ? `Pattern: ${consumptionPattern.pattern.replace(/_/g, ' ')} (${(consumptionPattern.confidence * 100).toFixed(0)}% confidence)`
                            : 'No consumption data available'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {consumptionPattern ? (
                          <div className="grid grid-cols-2 gap-3">
                            <div className={cn(PANEL, 'p-3.5')}>
                              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
                                Avg Daily
                              </p>
                              <p className="text-lg font-semibold">
                                {consumptionPattern.metrics.avgDailyConsumption.toFixed(2)}{' '}
                                <span className="text-sm font-normal text-muted-foreground">
                                  {item.unit}
                                </span>
                              </p>
                            </div>
                            <div className={cn(PANEL, 'p-3.5')}>
                              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
                                Std Deviation
                              </p>
                              <p className="text-lg font-semibold">
                                {consumptionPattern.metrics.stdDeviation.toFixed(2)}
                              </p>
                            </div>
                            <div className={cn(PANEL, 'p-3.5')}>
                              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
                                CV (Variability)
                              </p>
                              <p className="text-lg font-semibold">
                                {(consumptionPattern.metrics.coefficientOfVariation * 100).toFixed(
                                  1
                                )}
                                <span className="text-sm font-normal text-muted-foreground">%</span>
                              </p>
                            </div>
                            <div className={cn(PANEL, 'p-3.5')}>
                              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
                                Forecast Method
                              </p>
                              <p className="text-sm font-semibold capitalize">
                                {consumptionPattern.recommendedForecastMethod
                                  .replace(/_/g, ' ')
                                  .toLowerCase()}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <EmptyState
                            icon={BarChart3}
                            title="No consumption data"
                            description="Consumption patterns will appear after material usage is recorded."
                          />
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <h3 className="text-base font-semibold">Usage by Job Type</h3>
                        <CardDescription>Consumption rate by operation type</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {jobTypeRates.length > 0 ? (
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart
                              data={jobTypeRates}
                              layout="vertical"
                              margin={{ left: 80, right: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 12 }}
                                className="fill-muted-foreground"
                              />
                              <YAxis
                                type="category"
                                dataKey="jobType"
                                tick={{ fontSize: 12 }}
                                width={75}
                                className="fill-muted-foreground"
                              />
                              <RechartsTooltip
                                formatter={(value: number) => [
                                  `${value.toFixed(2)} ${item.unit}`,
                                  'Avg Rate',
                                ]}
                                contentStyle={{
                                  borderRadius: 8,
                                  border: '1px solid var(--border)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                }}
                              />
                              <Bar
                                dataKey="avgConsumptionRate"
                                fill={chartColors.primary}
                                radius={[0, 4, 4, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <EmptyState
                            icon={BarChart3}
                            title="No job type data"
                            description="Job type usage rates will appear after consumption records are logged."
                          />
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* ===== TAB 3: SUPPLY PIPELINE ===== */}
              <TabsContent value="supply-pipeline">
                <div className="space-y-6 mt-4">
                  {/* Summary Cards */}
                  <KPIGrid columns={3}>
                    <MetricCard
                      label="On Order"
                      value={supplyPipeline?.summary.onOrder || 0}
                      suffix={` ${item.unit}`}
                      sentiment="info"
                      icon={<ShoppingCart className="h-5 w-5" />}
                      subtitle={`${supplyPipeline?.purchaseOrders?.length || 0} open POs`}
                    />
                    <MetricCard
                      label="In Transit"
                      value={supplyPipeline?.summary.inTransit || 0}
                      suffix={` ${item.unit}`}
                      sentiment={supplyPipeline?.summary.inTransit ? 'success' : 'neutral'}
                      icon={<Truck className="h-5 w-5" />}
                      subtitle="Active shipments"
                    />
                    <MetricCard
                      label="Pending Receipt"
                      value={supplyPipeline?.summary.pendingReceipt || 0}
                      suffix={` ${item.unit}`}
                      sentiment={supplyPipeline?.summary.pendingReceipt ? 'warning' : 'neutral'}
                      icon={<ClipboardList className="h-5 w-5" />}
                      subtitle="Awaiting goods receipt"
                    />
                  </KPIGrid>

                  {/* PO Pipeline */}
                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Purchase Order Pipeline</h3>
                      <CardDescription>Open purchase orders for this item</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(supplyPipeline?.purchaseOrders?.length || 0) > 0 ? (
                        <DataTable
                          data={supplyPipeline!.purchaseOrders}
                          columns={getPOColumns()}
                          emptyTitle="No open POs"
                          emptyDescription=""
                        />
                      ) : (
                        <EmptyState
                          icon={ShoppingCart}
                          title="No open purchase orders"
                          description="There are no pending purchase orders for this item."
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* What is moving, and where it is. The two questions the
                      pitch is built on, and neither was answerable here before:
                      "in transit" was a hardcoded zero and the bin lived in a
                      table this page never read. */}
                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">In transit</h3>
                      <CardDescription>
                        Shipments carrying this item, with the carrier and when each is expected.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DataTable
                        data={supplyPipeline?.inboundShipments ?? []}
                        columns={getInboundColumns()}
                        emptyTitle="Nothing in transit"
                        emptyDescription="No shipment carrying this item is booked, moving or delayed. Shipments appear here from the moment one is booked until it is delivered."
                      />
                      {(supplyPipeline?.summary.inTransitWithoutEta ?? 0) > 0 && (
                        <p className="mt-3 text-xs text-status-warning-foreground">
                          {supplyPipeline!.summary.inTransitWithoutEta} of these carry no arrival
                          date. What is moving is known; when it lands is not.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Where it sits</h3>
                      <CardDescription>
                        The addressable positions holding this item, down to the bin.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DataTable
                        data={supplyPipeline?.binLocations ?? []}
                        columns={getBinColumns()}
                        emptyTitle="Not put away to a bin"
                        emptyDescription="This item is counted at site level but has not been put away to an addressable position. Stock received into a yard appears here once putaway is completed."
                      />
                    </CardContent>
                  </Card>

                  {/* Recent Receipts */}
                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Recent Goods Receipts</h3>
                      <CardDescription>Last 10 receipts for this item</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(supplyPipeline?.recentReceipts?.length || 0) > 0 ? (
                        <DataTable
                          data={supplyPipeline!.recentReceipts}
                          columns={getReceiptColumns()}
                          emptyTitle="No receipts"
                          emptyDescription=""
                        />
                      ) : (
                        <EmptyState
                          icon={ClipboardList}
                          title="No recent receipts"
                          description="No goods receipts recorded for this item yet."
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ===== TAB 4: TRANSACTIONS (Enhanced) ===== */}
              <TabsContent value="transactions">
                <div className="space-y-4 mt-4">
                  {/* Transaction Summary */}
                  {txnSummary &&
                    (() => {
                      const totalIn = Object.entries(txnSummary.byType)
                        .filter(([type]) => ['RECEIPT', 'RETURN', 'TRANSFER_IN'].includes(type))
                        .reduce((sum, [, v]) => sum + v.totalQty, 0);
                      const totalOut = Object.entries(txnSummary.byType)
                        .filter(([type]) => ['ISSUE', 'CONSUMPTION', 'SCRAP'].includes(type))
                        .reduce((sum, [, v]) => sum + v.totalQty, 0);
                      return (
                        <KPIGrid columns={3}>
                          <MetricCard
                            label="Total In"
                            value={totalIn}
                            prefix="+"
                            suffix={` ${item.unit}`}
                            sentiment="success"
                            icon={<ArrowDownLeft className="h-5 w-5" />}
                          />
                          <MetricCard
                            label="Total Out"
                            value={totalOut}
                            suffix={` ${item.unit}`}
                            sentiment="danger"
                            icon={<ArrowUpRight className="h-5 w-5" />}
                          />
                          <MetricCard
                            label="Transactions"
                            value={txnSummary.totalTransactions}
                            sentiment="info"
                            icon={<History className="h-5 w-5" />}
                          />
                        </KPIGrid>
                      );
                    })()}

                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold flex items-center gap-2">
                        <History className="h-5 w-5" />
                        Transaction Ledger
                      </h3>
                      <CardDescription>
                        Complete history of inventory movements for this item
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {transactionsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-muted-foreground">
                            Loading transactions...
                          </span>
                        </div>
                      ) : (
                        <DataTable
                          pagination
                          data={transactions}
                          columns={getTransactionColumns(item)}
                          emptyTitle="No transactions"
                          emptyDescription="No inventory transactions recorded for this item yet."
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ===== TAB 5: RESERVATIONS ===== */}
              <TabsContent value="reservations">
                <div className="space-y-4 mt-4">
                  <KPIGrid columns={3}>
                    <MetricCard
                      label="Active Reservations"
                      value={reservations.filter((r) => r.status === 'ACTIVE').length}
                      sentiment="info"
                      icon={<ClipboardList className="h-5 w-5" />}
                    />
                    <MetricCard
                      label="Total Reserved Qty"
                      value={reservations
                        .filter((r) => r.status === 'ACTIVE')
                        .reduce((s, r) => s + r.quantity, 0)}
                      suffix={` ${item.unit}`}
                      sentiment="neutral"
                    />
                    <MetricCard
                      label="Expiring Soon"
                      value={
                        reservations.filter(
                          (r) =>
                            r.status === 'ACTIVE' &&
                            r.expiresAt &&
                            new Date(r.expiresAt) <= new Date(Date.now() + 7 * 86400000)
                        ).length
                      }
                      sentiment="warning"
                      subtitle="Within 7 days"
                    />
                  </KPIGrid>

                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Active Reservations</h3>
                      <CardDescription>
                        Stock allocated to work orders, job kits, and shipments
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reservations.length > 0 ? (
                        <DataTable
                          pagination
                          data={reservations}
                          columns={getReservationColumns(item)}
                          emptyTitle="No reservations"
                          emptyDescription=""
                        />
                      ) : (
                        <EmptyState
                          icon={ClipboardList}
                          title="No reservations"
                          description="No stock has been reserved for this item."
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ===== TAB 6: BATCH & COMPLIANCE ===== */}
              <TabsContent value="batches">
                <div className="space-y-6 mt-4">
                  {/* Regulatory Info */}
                  {batchData?.regulatory && (
                    <Card className="border-l-4 border-l-status-info">
                      <CardContent className="py-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldCheck className="h-5 w-5 text-status-info" />
                          <span className="font-medium">Regulated Item</span>
                          <Badge variant="outline">
                            {batchData.regulatory.authority || 'Unknown Authority'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                          {batchData.regulatory.reference && (
                            <DetailRow label="Reference">
                              {batchData.regulatory.reference}
                            </DetailRow>
                          )}
                          {batchData.regulatory.minimumQty != null && (
                            <DetailRow label="Min Required Qty">
                              <span
                                className={
                                  totalQuantity >= (batchData.regulatory.minimumQty || 0)
                                    ? 'text-status-success'
                                    : 'text-status-destructive'
                                }
                              >
                                {batchData.regulatory.minimumQty} {item.unit}
                                {totalQuantity >= (batchData.regulatory.minimumQty || 0)
                                  ? ' ✓ Compliant'
                                  : ' ✗ Non-Compliant'}
                              </span>
                            </DetailRow>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Batch Summary */}
                  {batchData && batchData.batches.length > 0 && (
                    <KPIGrid columns={3}>
                      <MetricCard
                        label="Expired"
                        value={batchData.summary.expired}
                        sentiment={batchData.summary.expired > 0 ? 'danger' : 'success'}
                        subtitle={
                          batchData.summary.expired > 0 ? 'Requires attention' : 'None expired'
                        }
                      />
                      <MetricCard
                        label="Expiring Soon"
                        value={batchData.summary.expiringSoon}
                        sentiment={batchData.summary.expiringSoon > 0 ? 'warning' : 'success'}
                        subtitle="Within 30 days"
                      />
                      <MetricCard
                        label="Available"
                        value={batchData.summary.byStatus['AVAILABLE'] || 0}
                        sentiment="success"
                        subtitle={`${batchData.summary.total} ${pluralize(batchData.summary.total, 'batch', 'batches')} total`}
                      />
                    </KPIGrid>
                  )}

                  {/* Batch Table */}
                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Batch Inventory</h3>
                      <CardDescription>
                        All batches for this item with expiry tracking
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(batchData?.batches?.length || 0) > 0 ? (
                        <DataTable
                          pagination
                          data={batchData!.batches}
                          columns={getBatchColumns()}
                          emptyTitle="No batches"
                          emptyDescription=""
                        />
                      ) : (
                        <EmptyState
                          icon={Layers}
                          title="No batch data"
                          description="This item has no batch tracking records."
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ===== TAB 7: EQUIPMENT & SPARES ===== */}
              <TabsContent value="equipment">
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  {equipmentLinks && equipmentLinks.links.length > 0 && (
                    <Card className="border-l-4 border-l-primary">
                      <CardContent className="py-4 flex items-center gap-3">
                        <Wrench className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">
                            This item is a spare part for{' '}
                            <span className="font-bold">
                              {equipmentLinks.summary.totalEquipment}
                            </span>{' '}
                            equipment asset{equipmentLinks.summary.totalEquipment !== 1 ? 's' : ''}
                          </p>
                          {equipmentLinks.summary.criticalCount > 0 && (
                            <p className="text-sm text-status-destructive">
                              {equipmentLinks.summary.criticalCount} CRITICAL equipment depend
                              {equipmentLinks.summary.criticalCount === 1 ? 's' : ''} on this item
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <h3 className="text-base font-semibold">Linked Equipment</h3>
                      <CardDescription>
                        Equipment assets that use this item as a spare part
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(equipmentLinks?.links?.length || 0) > 0 ? (
                        <DataTable
                          data={equipmentLinks!.links}
                          columns={getEquipmentColumns()}
                          emptyTitle="No equipment"
                          emptyDescription=""
                        />
                      ) : (
                        <EmptyState
                          icon={Wrench}
                          title="No equipment links"
                          description="This item is not linked as a spare part to any equipment."
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          }
        />
      </motion.div>

      {/* Description */}
      {item.description && (
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground">Description</h3>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{item.description}</p>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.main>
  );
}

// ==============================================
// Helper Components
// ==============================================

/**
 * Everything about the item that stays true while the left column changes tabs.
 *
 * These figures used to live inside the Stock Levels tab, which meant that
 * switching to Usage & Trends hid the reorder point and lead time — the two
 * numbers a consumption trend is read against. A rail keeps them beside the
 * work instead of behind it.
 */
function ItemRail({
  item,
  levels,
  transactions,
  pipeline,
  pattern,
  safetyStock,
  spareRanking,
  totalQuantity,
  daysOfStock,
}: {
  item: InventoryItem;
  levels: InventoryLevel[];
  transactions: InventoryTransaction[];
  pipeline: SupplyPipeline | null;
  pattern: ConsumptionPattern | null;
  safetyStock: { calculatedSafetyStock: number } | null;
  spareRanking: CriticalSpareRanking | null;
  totalQuantity: number;
  daysOfStock: number | null;
}) {
  const balance = buildBalanceChartData(transactions);
  const avgDaily = pattern?.metrics?.avgDailyConsumption ?? 0;

  // Lead time only earns a bar chart when there is a spread to show. With a
  // single figure the bars would all be full width and say nothing.
  const leadRows =
    item.leadTimeDays != null
      ? [
          { label: 'Quoted lead time', value: item.leadTimeDays, className: 'bg-primary' },
          ...(item.safetyStockDays != null
            ? [
                {
                  label: 'Safety buffer',
                  value: item.safetyStockDays,
                  className: 'bg-status-info',
                },
              ]
            : []),
          ...(daysOfStock != null
            ? [
                {
                  label: 'Cover on hand',
                  value: daysOfStock,
                  className:
                    daysOfStock < (item.leadTimeDays ?? 0)
                      ? 'bg-status-destructive'
                      : 'bg-status-success',
                },
              ]
            : []),
        ]
      : [];

  // The bands the Critical Spares register uses, so a score reads the same on
  // both pages.
  const rankTone = {
    CRITICAL: 'destructive',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'default',
    NOT_ASSESSED: 'warning',
  } as const;

  return (
    <>
      {spareRanking && (
        <RailCard
          title="Criticality"
          icon={<ShieldAlert className="h-4 w-4 text-muted-foreground" />}
        >
          <DetailList
            items={[
              {
                label: 'Ranking',
                value:
                  spareRanking.criticalityScore != null
                    ? `${spareRanking.ranking} · ${spareRanking.criticalityScore.toFixed(1)}`
                    : 'Not assessed',
                tone: rankTone[spareRanking.ranking],
              },
              {
                // Where the consequence figure came from. A number set against
                // this item and one inherited from the assets it serves are
                // different claims, and used to look identical.
                label: 'Judged',
                value: spareRanking.criticality.provenance,
                when: !!spareRanking.criticality,
              },
              {
                label: 'Reason',
                value: spareRanking.criticality.note,
                when: !!spareRanking.criticality.note,
              },
              {
                label: 'Downtime cost',
                value: spareRanking.metrics.downtimeCostPerHour
                  ? `${formatCurrency(spareRanking.metrics.downtimeCostPerHour)}/hr`
                  : null,
                when: !!spareRanking.metrics.downtimeCostPerHour,
              },
              {
                // Not a link: the Equipment tab is on this same page, and the
                // page does not read a ?tab= param, so one would go nowhere.
                label: 'Equipment served',
                value: spareRanking.linkedEquipment.length,
                when: spareRanking.linkedEquipment.length > 0,
              },
              {
                label: 'MTBF',
                value: spareRanking.metrics.mtbf ? `${spareRanking.metrics.mtbf} h` : null,
                when: spareRanking.metrics.mtbf != null,
              },
              {
                label: 'MTTR',
                value: spareRanking.metrics.mttr ? `${spareRanking.metrics.mttr} h` : null,
                when: spareRanking.metrics.mttr != null,
              },
              {
                label: 'Recommended stock',
                // No recommendation where nobody judged the consequence. The
                // old code showed "0 – 1" here, which reads as advice to hold
                // almost none rather than as an absence of advice.
                value:
                  spareRanking.stockRecommendation.recommendedMinStock != null
                    ? `${spareRanking.stockRecommendation.recommendedMinStock} – ${spareRanking.stockRecommendation.recommendedMaxStock}`
                    : spareRanking.stockRecommendation.basis,
              },
              {
                label: 'Inspect',
                value: spareRanking.stockRecommendation.inspectionFrequency,
                when: !!spareRanking.stockRecommendation.inspectionFrequency,
              },
              {
                label: 'Second source',
                value: 'Required',
                tone: 'warning',
                when: spareRanking.stockRecommendation.multiSourceRequired,
              },
            ]}
          />
          {spareRanking.riskFactors.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t pt-3">
              {spareRanking.riskFactors.map((f) => (
                <Badge key={f} variant="outline" className="text-xs">
                  {f}
                </Badge>
              ))}
            </div>
          )}
        </RailCard>
      )}

      {leadRows.length > 0 && (
        <RailCard title="Lead time" icon={<Clock className="h-4 w-4 text-muted-foreground" />}>
          <MetricBars rows={leadRows} unit="days" />
          {daysOfStock != null && item.leadTimeDays != null && daysOfStock < item.leadTimeDays && (
            // The comparison is the point: cover shorter than lead time means
            // reordering today still leaves a gap.
            <p className="mt-3 text-xs text-status-destructive-foreground">
              Cover runs out {item.leadTimeDays - daysOfStock} days before a replacement order could
              arrive.
            </p>
          )}
        </RailCard>
      )}

      {/* Two points draw a straight diagonal, which reads as a trend when it is
          only a line between two facts. Three is the fewest that can show a
          shape. */}
      {balance.length >= 3 && (
        <RailCard
          title="Stock balance"
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
        >
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={balance} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id="railBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <RechartsTooltip
                  contentStyle={{ fontSize: 12 }}
                  labelStyle={{ fontSize: 11 }}
                  formatter={(v: number) => [`${formatNumber(v)} ${item.unit}`, 'Balance']}
                />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#railBalance)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {balance.length} recorded {pluralize(balance.length, 'movement')}
            {avgDaily > 0 && ` · ${avgDaily.toFixed(1)} ${item.unit}/day average`}
          </p>
        </RailCard>
      )}

      <RailCard title="Item details" icon={<Package className="h-4 w-4 text-muted-foreground" />}>
        <DetailList
          items={[
            { label: 'Category', value: item.category },
            { label: 'Subcategory', value: item.subcategory, when: !!item.subcategory },
            { label: 'Unit', value: item.unit },
            { label: 'Unit cost', value: formatCurrency(item.unitCost || 0) },
            {
              label: 'Available to order',
              value: `${formatNumber(pipeline?.summary?.onOrder ?? 0)} ${item.unit}`,
              tone: 'success',
              when: !!pipeline,
            },
            {
              label: 'In transit',
              value: `${formatNumber(pipeline?.summary?.inTransit ?? 0)} ${item.unit}`,
              tone: 'info',
              when: !!pipeline,
            },
            {
              label: 'Pending receipt',
              value: `${formatNumber(pipeline?.summary?.pendingReceipt ?? 0)} ${item.unit}`,
              when: !!pipeline,
            },
            { label: 'Locations', value: levels.length },
          ]}
        />

        <div className="mt-4 border-t pt-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Thresholds
          </p>
          <DetailList
            items={[
              { label: 'Minimum', value: `${formatNumber(item.minStockLevel)} ${item.unit}` },
              {
                label: 'Reorder point',
                value: `${formatNumber(item.reorderPoint ?? 0)} ${item.unit}`,
                tone:
                  item.reorderPoint != null && totalQuantity < item.reorderPoint
                    ? 'destructive'
                    : 'default',
                when: item.reorderPoint != null,
              },
              {
                label: 'Maximum',
                value: `${formatNumber(item.maxStockLevel ?? 0)} ${item.unit}`,
                when: item.maxStockLevel != null,
              },
              {
                label: 'Safety stock',
                value: `${formatNumber(safetyStock?.calculatedSafetyStock ?? 0)} ${item.unit}`,
                tone:
                  safetyStock && totalQuantity < safetyStock.calculatedSafetyStock
                    ? 'warning'
                    : 'default',
                when: !!safetyStock,
              },
            ]}
          />
        </div>

        {item.isRegulated && (
          <div className="mt-4 border-t pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Regulatory
            </p>
            <DetailList
              items={[
                { label: 'Authority', value: item.regulatoryAuthority || '—' },
                {
                  label: 'Mandatory minimum',
                  value: `${formatNumber(item.regulatoryMinQty ?? 0)} ${item.unit}`,
                  tone:
                    item.regulatoryMinQty != null && totalQuantity < item.regulatoryMinQty
                      ? 'destructive'
                      : 'default',
                  when: item.regulatoryMinQty != null,
                },
                {
                  label: 'Reference',
                  value: item.regulatoryReference,
                  when: !!item.regulatoryReference,
                },
              ]}
            />
          </div>
        )}

        {item.description && (
          <div className="mt-4 border-t pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Description
            </p>
            <p className="text-sm text-muted-foreground">{item.description}</p>
          </div>
        )}
      </RailCard>
    </>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-medium text-sm">{children}</span>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Icon className="h-10 w-10 text-muted-foreground/50 mb-3" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

function StockGauge({
  current,
  min,
  reorderPoint,
  max,
  unit,
  status,
}: {
  current: number;
  min: number;
  reorderPoint?: number | null;
  max?: number | null;
  unit: string;
  /** The server's stock status. See the comment on `zone` for why it is passed in. */
  status?: string;
}) {
  const effectiveMax = max || Math.max(current * 1.5, min * 3);
  const percentage = Math.min((current / effectiveMax) * 100, 100);
  const minPct = (min / effectiveMax) * 100;
  const reorderPct = reorderPoint ? (reorderPoint / effectiveMax) * 100 : minPct;
  const optimalEnd = Math.min(80, 100); // optimal zone ends at ~80%

  // The badge reports the server's status rather than recomputing one here.
  //
  // This gauge used to apply its own thresholds — Overstock above 90% of max,
  // Critical at or below min — while the server called Overstock only above max
  // and Low only below min. The two sat on the same screen roughly 200px apart
  // and disagreed: 39 of a 40 maximum read "Overstock" here and "OPTIMAL" in the
  // details table beneath it. Where stock sits against its thresholds is a
  // business rule, so it has one owner, and this draws what it is told.
  const zone = status ?? 'Optimal';
  const zoneStyles: Record<string, string> = {
    Stockout: 'bg-danger-50 text-danger-500 border-danger-200',
    Low: 'bg-warning-50 text-warning-600 border-warning-200',
    Optimal: 'bg-success-50 text-success-600 border-success-200',
    Overstock: 'bg-primary-50 text-primary-500 border-primary-200',
  };

  return (
    <Card className="mt-4 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Gauge className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Stock Level</h4>
          <Badge className={cn('text-[11px] border', zoneStyles[zone])}>{zone}</Badge>
        </div>
        <span className="text-sm">
          <span className="font-semibold">{formatNumber(current)}</span>
          <span className="text-muted-foreground">
            {' '}
            / {formatNumber(effectiveMax)} {unit}
          </span>
        </span>
      </div>

      {/* Marker + gauge */}
      <div className="relative pt-6 pb-1">
        {/* Triangle marker */}
        <div
          className="absolute top-0 flex flex-col items-center transition-all duration-500 ease-out"
          style={{ left: `${percentage}%`, transform: 'translateX(-50%)' }}
        >
          <span className="text-[10px] font-bold text-foreground mb-0.5">
            {formatNumber(current)}
          </span>
          <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-foreground" />
        </div>

        {/* Gauge bar */}
        <div className="relative h-3 rounded-full overflow-hidden bg-muted/60">
          <div className="absolute inset-0 flex">
            <div className="bg-status-destructive-subtle" style={{ width: `${minPct}%` }} />
            <div
              className="bg-status-warning-subtle"
              style={{ width: `${reorderPct - minPct}%` }}
            />
            <div
              className="bg-status-success-subtle"
              style={{ width: `${optimalEnd - reorderPct}%` }}
            />
            <div className="bg-status-info-subtle" style={{ width: `${100 - optimalEnd}%` }} />
          </div>
        </div>

        {/* Tick marks + labels */}
        <div className="relative h-4 mt-0.5">
          {/* Min tick */}
          <div
            className="absolute flex flex-col items-center"
            style={{ left: `${minPct}%`, transform: 'translateX(-50%)' }}
          >
            <div className="w-px h-1.5 bg-muted-foreground/40" />
            <span className="text-[9px] text-muted-foreground mt-0.5">Min: {min}</span>
          </div>
          {/* Reorder tick */}
          {reorderPoint && reorderPct !== minPct && (
            <div
              className="absolute flex flex-col items-center"
              style={{ left: `${reorderPct}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-px h-1.5 bg-muted-foreground/40" />
              <span className="text-[9px] text-muted-foreground mt-0.5">
                Reorder: {reorderPoint}
              </span>
            </div>
          )}
          {/* Max label on right */}
          <span className="absolute right-0 text-[9px] text-muted-foreground mt-0.5">
            Max: {formatNumber(effectiveMax)}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ==============================================
// Column Definitions
// ==============================================

function getLevelColumns(item: InventoryItem): Column<InventoryLevel>[] {
  return [
    {
      key: 'location',
      header: 'Location',
      render: (level) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <div className="font-medium">{level.location?.name || '-'}</div>
            <div className="text-xs leading-4 text-muted-foreground">{level.location?.code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (level) => (
        <Badge className={locationTypeColors[level.location?.type || ''] || ''}>
          {level.location?.type || '-'}
        </Badge>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (level) => (
        <span className="font-medium">
          {level.quantity} {item.unit}
        </span>
      ),
    },
    {
      key: 'reserved',
      header: 'Reserved',
      render: (level) => (
        <span className="text-muted-foreground">
          {level.reservedQty} {item.unit}
        </span>
      ),
    },
    {
      key: 'consigned',
      header: 'Customer-owned',
      // Only ever non-zero at a consignment node, where title has passed but
      // the material is still standing there. Without this column the quantity
      // above reads as ours when part of it is not.
      render: (level) =>
        level.customerOwnedQty ? (
          <span className="font-medium text-status-info-foreground">
            {level.customerOwnedQty} {item.unit}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'available',
      header: 'Available',
      render: (level) => (
        <span className="font-medium text-success-600">
          {level.availableQty} {item.unit}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (level) => <StatusBadge status={statusMap[level.status || 'Optimal'] || 'optimal'} />,
    },
  ];
}

/** Stock in reads as success, stock out as destructive, the rest informational. */
const txnTypeColors: Record<string, string> = {
  RECEIPT: 'bg-status-success-subtle text-status-success-foreground',
  RETURN: 'bg-status-success-subtle text-status-success-foreground',
  TRANSFER_IN: 'bg-status-success-subtle text-status-success-foreground',
  ISSUE: 'bg-status-destructive-subtle text-status-destructive-foreground',
  CONSUMPTION: 'bg-status-destructive-subtle text-status-destructive-foreground',
  SCRAP: 'bg-status-destructive-subtle text-status-destructive-foreground',
  COUNT: 'bg-status-info-subtle text-status-info-foreground',
  ADJUSTMENT: 'bg-status-info-subtle text-status-info-foreground',
  RESERVE: 'bg-status-neutral-subtle text-status-neutral-foreground',
  UNRESERVE: 'bg-status-neutral-subtle text-status-neutral-foreground',
};
const positiveTypes = ['RECEIPT', 'RETURN', 'TRANSFER_IN', 'UNRESERVE'];

function getTransactionColumns(item: InventoryItem): Column<InventoryTransaction>[] {
  return [
    {
      key: 'createdAt',
      header: 'Date',
      render: (txn) => (
        <span className="text-sm">
          {formatDate(txn.createdAt)}{' '}
          <span className="text-muted-foreground">
            {new Date(txn.createdAt).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (txn) => (
        <Badge
          className={
            txnTypeColors[txn.type] || 'bg-status-neutral-subtle text-status-neutral-foreground'
          }
        >
          {txn.type.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (txn) => {
        const isPositive = positiveTypes.includes(txn.type) || txn.quantity > 0;
        return (
          <div className="flex items-center gap-1">
            {isPositive ? (
              <ArrowDownLeft className="h-3.5 w-3.5 text-status-success" />
            ) : (
              <ArrowUpRight className="h-3.5 w-3.5 text-status-destructive" />
            )}
            <span
              className={
                isPositive
                  ? 'font-medium text-status-success'
                  : 'font-medium text-status-destructive'
              }
            >
              {isPositive ? '+' : ''}
              {txn.quantity} {item.unit}
            </span>
          </div>
        );
      },
    },
    {
      key: 'balanceAfter',
      header: 'Balance After',
      render: (txn) => (
        <span className="font-medium">
          {txn.balanceAfter} {item.unit}
        </span>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (txn) => <span className="text-sm">{txn.location?.name || '-'}</span>,
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (txn) => (
        <span className="text-sm text-muted-foreground">
          {txn.referenceType && txn.referenceId
            ? `${txn.referenceType} #${txn.referenceId.slice(0, 8)}`
            : txn.referenceType || '-'}
        </span>
      ),
    },
    {
      key: 'performedBy',
      header: 'Performed By',
      render: (txn) => <span className="text-sm">{txn.performedBy?.fullName || '-'}</span>,
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (txn) => (
        <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
          {txn.notes || '-'}
        </span>
      ),
    },
  ];
}

function getPOColumns(): Column<SupplyPipeline['purchaseOrders'][number]>[] {
  return [
    {
      key: 'poNumber',
      header: 'PO Number',
      render: (po) => (
        <Link
          to={`/purchase-orders/${po.poId}`}
          className="text-primary hover:underline font-medium"
        >
          {po.poNumber}
        </Link>
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (po) => <span>{po.supplier?.name || '-'}</span>,
    },
    {
      key: 'quantity',
      header: 'Qty Ordered',
      render: (po) => <span className="font-medium">{po.quantity}</span>,
    },
    { key: 'receivedQty', header: 'Qty Received', render: (po) => <span>{po.receivedQty}</span> },
    {
      key: 'expectedDate',
      header: 'Expected Date',
      render: (po) => (
        <span className="text-sm">{po.expectedDate ? formatDate(po.expectedDate) : '-'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (po) => <Badge variant="outline">{po.status.replace(/_/g, ' ')}</Badge>,
    },
  ];
}

/**
 * The shipments carrying this item.
 *
 * Two things are said plainly rather than tidied away. A carrier that was never
 * matched to a record shows as the text somebody typed, marked unlinked — the
 * alternative is a blank that reads as "no carrier". And a shipment with no
 * estimated arrival says so, because a date computed to fill the gap would be
 * planned around.
 */
function getInboundColumns(): Column<InboundShipment>[] {
  return [
    {
      key: 'shipmentNumber',
      header: 'Shipment',
      sortable: true,
      linkTo: (s) => `/shipments/${s.id}`,
      render: (s) => (
        <div>
          <div className="font-medium">{s.shipmentNumber}</div>
          <div className="text-xs text-muted-foreground">
            {s.purchaseOrder ? s.purchaseOrder.poNumber : 'internal transfer'}
          </div>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty of this item',
      sortable: true,
      className: 'text-right tabular-nums',
    },
    {
      key: 'route',
      header: 'Route',
      accessorFn: (s) => s.destination?.code ?? '',
      render: (s) => (
        <span className="text-sm">
          {s.origin?.code ?? '—'} → {s.destination?.code ?? '—'}
        </span>
      ),
    },
    {
      key: 'carrier',
      header: 'Carrier',
      accessorFn: (s) => s.carrier?.name ?? s.carrierText ?? '',
      render: (s) =>
        s.carrier ? (
          <Link
            to={`/tms/carriers/${s.carrier.id}`}
            className="text-primary underline-offset-4 hover:underline"
          >
            {s.carrier.name}
          </Link>
        ) : s.carrierText ? (
          <span>
            {s.carrierText}
            <span className="ml-1 text-[11px] text-status-warning-foreground">not linked</span>
          </span>
        ) : (
          <span className="text-muted-foreground">none recorded</span>
        ),
    },
    { key: 'status', header: 'Status', sortable: true },
    {
      key: 'estimatedArrival',
      header: 'Expected',
      sortable: true,
      accessorFn: (s) => s.estimatedArrival ?? '',
      render: (s) =>
        s.estimatedArrival ? (
          <div>
            <div className="text-sm">{formatDate(s.estimatedArrival)}</div>
            {s.latestMilestone && (
              <div className="text-[11px] text-muted-foreground">{s.latestMilestone.milestone}</div>
            )}
          </div>
        ) : (
          <span className="text-xs text-status-warning-foreground">no ETA given</span>
        ),
    },
  ];
}

/**
 * The addressable positions holding it.
 *
 * The address is assembled from whatever parts the yard recorded. Some storage is
 * zone-level and has no aisle, so a missing part is left out rather than filled
 * with a dash that suggests the yard failed to record it.
 */
function getBinColumns(): Column<BinLocation>[] {
  const address = (b: BinLocation) =>
    [b.zone, b.aisle, b.rack, b.level].filter(Boolean).join(' · ');
  return [
    {
      key: 'code',
      header: 'Position',
      sortable: true,
      linkTo: (b) => `/yard/locations/${b.storageLocationId}`,
      render: (b) => (
        <div>
          <div className="font-medium">{b.code}</div>
          <div className="text-xs text-muted-foreground">{address(b) || b.name}</div>
        </div>
      ),
    },
    {
      key: 'site',
      header: 'Site',
      accessorFn: (b) => b.site?.code ?? '',
      render: (b) => b.site?.code ?? '—',
    },
    { key: 'quantity', header: 'Quantity', sortable: true, className: 'text-right tabular-nums' },
    {
      key: 'reservedQty',
      header: 'Reserved',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (b) =>
        b.reservedQty > 0 ? b.reservedQty : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'batch',
      header: 'Batch',
      accessorFn: (b) => b.batch?.batchNumber ?? '',
      render: (b) =>
        b.batch ? (
          <span className="font-mono text-xs">{b.batch.batchNumber}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];
}

function getReceiptColumns(): Column<SupplyPipeline['recentReceipts'][number]>[] {
  const qsColors: Record<string, string> = {
    PASSED: 'bg-status-success-subtle text-status-success-foreground',
    FAILED: 'bg-status-destructive-subtle text-status-destructive-foreground',
    PENDING_INSPECTION: 'bg-status-warning-subtle text-status-warning-foreground',
    CONDITIONAL: 'bg-status-info-subtle text-status-info-foreground',
  };
  return [
    {
      key: 'receiptNumber',
      header: 'Receipt #',
      render: (r) => <span className="font-medium">{r.receiptNumber}</span>,
    },
    {
      key: 'receivedAt',
      header: 'Date',
      render: (r) => <span className="text-sm">{formatDate(r.receivedAt)}</span>,
    },
    { key: 'location', header: 'Location', render: (r) => <span>{r.location.name}</span> },
    {
      key: 'receivedQty',
      header: 'Received',
      render: (r) => <span className="font-medium">{r.receivedQty}</span>,
    },
    {
      key: 'acceptedQty',
      header: 'Accepted',
      render: (r) => <span className="text-status-success">{r.acceptedQty}</span>,
    },
    {
      key: 'rejectedQty',
      header: 'Rejected',
      render: (r) => (
        <span className={r.rejectedQty > 0 ? 'text-status-destructive' : ''}>{r.rejectedQty}</span>
      ),
    },
    {
      key: 'qualityStatus',
      header: 'QC Status',
      render: (r) => (
        <Badge className={qsColors[r.qualityStatus] || ''}>
          {r.qualityStatus.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'batchNumber',
      header: 'Batch',
      render: (r) => <span className="text-sm text-muted-foreground">{r.batchNumber || '-'}</span>,
    },
  ];
}

function getReservationColumns(item: InventoryItem): Column<StockReservation>[] {
  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-status-info-subtle text-status-info-foreground',
    FULFILLED: 'bg-status-success-subtle text-status-success-foreground',
    RELEASED: 'bg-status-neutral-subtle text-status-neutral-foreground',
    EXPIRED: 'bg-status-destructive-subtle text-status-destructive-foreground',
  };
  return [
    {
      key: 'referenceType',
      header: 'Type',
      render: (r) => <Badge variant="outline">{r.referenceType}</Badge>,
    },
    {
      key: 'referenceId',
      header: 'Reference',
      render: (r) => <span className="text-sm font-mono">{r.referenceId?.slice(0, 8) || '-'}</span>,
    },
    { key: 'location', header: 'Location', render: (r) => <span>{r.location?.name || '-'}</span> },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (r) => (
        <span className="font-medium">
          {r.quantity} {item.unit}
        </span>
      ),
    },
    {
      key: 'reservedBy',
      header: 'Reserved By',
      render: (r) => <span className="text-sm">{r.reservedBy?.fullName || '-'}</span>,
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      render: (r) => (
        <span className="text-sm">{r.expiresAt ? formatDate(r.expiresAt) : 'Never'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge className={statusColors[r.status] || ''}>{r.status}</Badge>,
    },
  ];
}

function getBatchColumns(): Column<ItemBatch>[] {
  const batchStatusColors: Record<string, string> = {
    AVAILABLE: 'bg-status-success-subtle text-status-success-foreground',
    RESERVED: 'bg-status-info-subtle text-status-info-foreground',
    QUARANTINE: 'bg-status-warning-subtle text-status-warning-foreground',
    EXPIRED: 'bg-status-destructive-subtle text-status-destructive-foreground',
    CONSUMED: 'bg-status-neutral-subtle text-status-neutral-foreground',
  };
  return [
    {
      key: 'batchNumber',
      header: 'Batch #',
      render: (b) => <span className="font-medium font-mono">{b.batchNumber}</span>,
    },
    { key: 'location', header: 'Location', render: (b) => <span>{b.location?.name || '-'}</span> },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (b) => <span className="font-medium">{b.quantity}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (b) => <Badge className={batchStatusColors[b.status] || ''}>{b.status}</Badge>,
    },
    {
      key: 'receivedDate',
      header: 'Received',
      render: (b) => <span className="text-sm">{formatDate(b.receivedDate)}</span>,
    },
    {
      key: 'expiryDate',
      header: 'Expiry',
      render: (b) => {
        if (!b.expiryDate) return <span className="text-muted-foreground">—</span>;
        const exp = new Date(b.expiryDate);
        const now = new Date();
        const daysUntil = Math.ceil((exp.getTime() - now.getTime()) / 86400000);
        return (
          <span
            className={
              daysUntil < 0
                ? 'text-status-destructive font-medium'
                : daysUntil < 30
                  ? 'text-status-warning'
                  : ''
            }
          >
            {formatDate(exp)}
            {daysUntil < 0 ? ' (Expired)' : daysUntil < 30 ? ` (${daysUntil}d)` : ''}
          </span>
        );
      },
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (b) => <span className="text-sm">{b.supplier?.name || '-'}</span>,
    },
    {
      key: 'certificateNumber',
      header: 'Certificate',
      render: (b) => (
        <span className="text-sm text-muted-foreground">{b.certificateNumber || '-'}</span>
      ),
    },
  ];
}

function getEquipmentColumns(): Column<EquipmentLink>[] {
  const critColors: Record<string, string> = {
    CRITICAL: 'bg-status-destructive-subtle text-status-destructive-foreground',
    HIGH: 'bg-status-warning-subtle text-status-warning-foreground',
    MEDIUM: 'bg-status-info-subtle text-status-info-foreground',
    LOW: 'bg-status-neutral-subtle text-status-neutral-foreground',
  };
  return [
    {
      key: 'equipmentName',
      header: 'Equipment',
      render: (e) => (
        <Link
          to={`/equipment/${e.equipmentId}`}
          className="text-primary hover:underline font-medium"
        >
          {e.equipmentName}
        </Link>
      ),
    },
    {
      key: 'assetNumber',
      header: 'Asset #',
      render: (e) => <span className="font-mono text-sm">{e.assetNumber}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      render: (e) => <Badge variant="outline">{e.category}</Badge>,
    },
    {
      key: 'criticalityLevel',
      header: 'Criticality',
      render: (e) => (
        <Badge className={critColors[e.criticalityLevel || 'LOW'] || ''}>
          {e.criticalityLevel || '-'}
        </Badge>
      ),
    },
    {
      key: 'recommendedQty',
      header: 'Rec. Qty',
      render: (e) => <span className="font-medium">{e.recommendedQty}</span>,
    },
    {
      key: 'equipmentStatus',
      header: 'Status',
      render: (e) => <Badge variant="outline">{e.equipmentStatus}</Badge>,
    },
  ];
}

// ==============================================
// Chart Data Helpers
// ==============================================

function buildBalanceChartData(txns: InventoryTransaction[]) {
  // Group by day, take last balance of each day
  const byDay = new Map<string, number>();
  const sorted = [...txns].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  for (const txn of sorted) {
    const date = new Date(txn.createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    byDay.set(date, txn.balanceAfter);
  }
  return Array.from(byDay.entries()).map(([date, balance]) => ({ date, balance }));
}
